import { exec, getApiToken } from '../utils';
import type { CatalogRule, ConditionItem, KeyValuePair, LogicalOperator } from '@src/components/catalog/CatalogRules/types';
import { generateId, isFamilyField, isJsonField } from '@src/components/catalog/CatalogRules/types';

interface BackendCondition {
  field?: string;
  logicalJoin?: string;
  operator?: string;
  value?: string | Record<string, unknown>;
  conditions?: BackendCondition[];
}

interface BackendRule {
  id: number;
  position: number;
  enabled?: boolean;
  conditions: BackendCondition[];
  actions: Array<{ field: string, value: string | Record<string, unknown> }>;
}

interface GetCatalogRulesArgs {
  partnerCode: string;
  token: string;
}

interface GetCatalogRulesExecResponse {
  restUser: {
    partner: {
      catalogRules: BackendRule[];
    };
  };
}

const deserializePairs = (value: string | Record<string, unknown>, family: boolean): KeyValuePair[] => {
  try {
    const obj = typeof value === 'string' ? JSON.parse(value) as Record<string, unknown> : value;
    return Object.entries(obj).map(([k, v]) => ({
      id: generateId(),
      key: k,
      value: family ? '' : String(v ?? ''),
      values: family && Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [],
    }));
  } catch {
    return [{ id: generateId(), key: '', value: family ? '' : String(value), values: [] }];
  }
};

const deserializeConditionItem = (c: BackendCondition): ConditionItem => {
  if (c.conditions) {
    const firstChildJoin = c.conditions[0]?.logicalJoin as LogicalOperator | undefined;
    return {
      id: generateId(),
      type: 'group',
      logicalOperator: (c.logicalJoin as LogicalOperator) || 'and',
      matchType: firstChildJoin === 'or' ? 'any' : 'all',
      children: c.conditions.map(deserializeConditionItem),
    };
  }

  const field = c.field ?? '';
  return {
    id: generateId(),
    type: 'condition',
    logicalOperator: (c.logicalJoin as LogicalOperator) || 'and',
    field,
    operator: c.operator ?? '',
    value: isJsonField(field) ? '' : String(c.value ?? ''),
    pairs: isJsonField(field) ? deserializePairs(c.value ?? {}, isFamilyField(field)) : [],
  };
};

const toFrontendRules = (rules: BackendRule[]): CatalogRule[] =>
  rules
    .sort((a, b) => a.position - b.position)
    .map(rule => ({
      id: String(rule.id),
      enabled: rule.enabled !== false,
      conditions: rule.conditions.map(deserializeConditionItem),
      actions: rule.actions.map(a => ({
        id: generateId(),
        field: a.field,
        value: isJsonField(a.field) ? '' : String(a.value),
        pairs: isJsonField(a.field) ? deserializePairs(a.value, isFamilyField(a.field)) : [],
      })),
    }));

/**
 * Gets catalog rules for a partner.
 * @param partnerCode - The partner code to get rules for
 * @returns Promise resolving to the array of catalog rule groups
 */
export const getCatalogRules = async (
  partnerCode: string,
): Promise<CatalogRule[]> => {
  const apiToken = await getApiToken();

  const conditionFields = `
    field logicalJoin operator value
    conditions {
      field logicalJoin operator value
      conditions {
        field logicalJoin operator value
      }
    }
  `;
  const query = `
    query catalogRules($partnerCode: String!, $token: String!) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          catalogRules {
            id
            position
            enabled
            conditions { ${conditionFields} }
            actions {
              field
              value
            }
          }
        }
      }
    }
  `;

  const data = await exec<GetCatalogRulesArgs, GetCatalogRulesExecResponse>(
    query,
    { variables: { token: apiToken, partnerCode } },
  );

  return toFrontendRules(data.restUser.partner.catalogRules ?? []);
};
